import type { ComponentType } from 'react';
import {
	IconActivities,
	IconAdventure,
	IconArtsCulture,
	IconAwardWinningSpa,
	IconBeach,
	IconCulinaryExperience,
	IconDining,
	IconDivingSnorkeling,
	IconExperience,
	IconGolf,
	IconOutdoorExperience,
	IconWellness,
} from '../icon';
import type { SvgIconProps } from '../icon/icon-props';

/**
 * BL AC "Taxonomy tag: the Taxonomy field + icon > Use L3 subcategory".
 * Example: `fourseasons:global-data/product_taxonomy/experiences/aquatic_escapades/snorkelling`
 * gives L1 `experiences`, L2 `aquatic_escapades`, L3 `snorkelling`.
 *
 * Returns `undefined` when the authored path does not reach L3, so no tag is
 * rendered (UI AC 3b: "If a tag is not authored, no empty placeholder is shown").
 */
export function getTaxonomyL3(taxonomy?: string): string | undefined {
	if (!taxonomy) return undefined;

	const path = taxonomy.includes(':') ? taxonomy.slice(taxonomy.indexOf(':') + 1) : taxonomy;
	const segments = path.split('/').filter(Boolean);
	const rootIndex = segments.indexOf('product_taxonomy');
	const levels = rootIndex >= 0 ? segments.slice(rootIndex + 1) : segments;

	return levels.length >= 3 ? levels[2] : undefined;
}

export function formatTaxonomyLabel(level: string): string {
	return level
		.split(/[_-]+/)
		.filter(Boolean)
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

/**
 * UI AC 3e: the category icon is "set at the taxonomy level for the experience's
 * L3 category — not configurable per experience", so the mapping lives here and
 * is never an authored prop.
 */
const TAXONOMY_ICONS: Record<string, ComponentType<SvgIconProps>> = {
	snorkelling: IconDivingSnorkeling,
	snorkeling: IconDivingSnorkeling,
	diving: IconDivingSnorkeling,
	beach: IconBeach,
	golf: IconGolf,
	spa: IconAwardWinningSpa,
	wellness: IconWellness,
	dining: IconDining,
	culinary: IconCulinaryExperience,
	arts_culture: IconArtsCulture,
	adventure: IconAdventure,
	outdoor: IconOutdoorExperience,
	activities: IconActivities,
};

export function toTaxonomyKey(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

export function getTaxonomyIcon(level: string): ComponentType<SvgIconProps> {
	return TAXONOMY_ICONS[toTaxonomyKey(level)] ?? IconExperience;
}

export function resolveTaxonomyLabel(taxonomy?: string): string | undefined {
	if (!taxonomy) return undefined;
	const l3 = getTaxonomyL3(taxonomy);
	if (l3) return formatTaxonomyLabel(l3);
	return taxonomy.includes('/') ? undefined : taxonomy.trim() || undefined;
}
